import type { z } from 'zod';
import { ApiError, problemSchema } from './problem';

// The typed fetch wrapper over the internal API (07 §9). Same origin: in dev
// Vite proxies /internal, /blobs, /docs, /readyz to the API on :7800; in
// stage/prod Caddy does. The session is the httpOnly `plancia_session` cookie.
//
// Types: hand-written Zod schemas (src/lib/api/schemas.ts) over the API's DTOs.
// The OpenAPI document at /docs/openapi.json describes the routes but not the
// response bodies yet (the Nest controllers return plain objects without
// @ApiResponse types), so openapi-typescript would generate `unknown` for every
// response; see apps/web/README.md.

type UnauthorizedListener = (error: ApiError) => void;
let onUnauthorized: UnauthorizedListener | undefined;

/** The auth layer listens for 401s on any call except the auth endpoints themselves. */
export function setUnauthorizedListener(listener: UnauthorizedListener | undefined): void {
  onUnauthorized = listener;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  schema?: z.ZodType<T>;
  signal?: AbortSignal;
  /** Do not report a 401 to the auth layer (login, me). */
  quiet401?: boolean;
}

export async function api<T = unknown>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, signal, quiet401 = false } = options;
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json, application/problem+json',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'network', { detail: cause instanceof Error ? cause.message : String(cause) });
  }

  if (!response.ok) {
    let problem = {};
    try {
      const parsed = problemSchema.safeParse(await response.json());
      if (parsed.success) problem = parsed.data;
    } catch {
      /* not JSON: keep the status only */
    }
    const error = new ApiError(
      response.status,
      (problem as { code?: string }).code ?? `http_${response.status}`,
      problem,
    );
    if (response.status === 401 && !quiet401) onUnauthorized?.(error);
    throw error;
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  return schema ? schema.parse(data) : (data as T);
}
